// Module 8 — Parameter Provenance section on the RunDetailPage.
// Lazy-loads when expanded. Groups records by collection → fields with
// {value, unit, source, confidence, citation, updated_by, updated_at}.
import { useCallback, useState } from "react";
import { ChevronDown, ChevronRight, BookOpen } from "lucide-react";
import { runs as runsApi } from "@/api/endpoints";
import Card from "@/components/Card";
import SpinnerButton from "@/components/SpinnerButton";
import { formatApiError } from "@/lib/formatApiError";

const CONFIDENCE_TONE = {
    reviewed: "text-ux-success bg-ux-success/10 border-ux-success/30",
    provisional: "text-ux-warning bg-ux-warning/10 border-ux-warning/30",
    low: "text-ux-danger bg-ux-danger/10 border-ux-danger/30",
    default: "text-ux-text-3 bg-ux-bg-2 border-ux-border-1",
};

function ConfidenceChip({ level }) {
    const tone = CONFIDENCE_TONE[level] || CONFIDENCE_TONE.default;
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${tone}`}
            data-testid={`provenance-conf-${level || "unknown"}`}
        >
            {level || "—"}
        </span>
    );
}

function SourceChip({ source }) {
    const toneMap = {
        preset: "text-ux-accent bg-ux-accent/10 border-ux-accent/30",
        user: "text-ux-text-2 bg-ux-bg-2 border-ux-border-2",
        imported: "text-sky-400 bg-sky-500/10 border-sky-500/30",
    };
    const tone = toneMap[source] || "text-ux-text-3 bg-ux-bg-2 border-ux-border-1";
    return (
        <span
            className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${tone}`}
        >
            {source || "—"}
        </span>
    );
}

function renderValue(r) {
    if (r.value === null || r.value === undefined) return "—";
    const v = typeof r.value === "number"
        ? (Math.abs(r.value) < 1e-3 ? r.value.toExponential(2) : r.value.toFixed(3).replace(/\.?0+$/, ""))
        : String(r.value);
    return r.unit ? `${v} ${r.unit}` : v;
}

export default function ParameterProvenanceSection({ runId }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const d = await runsApi.parameterProvenance(runId);
            setData(d);
        } catch (e) {
            setErr(formatApiError(e, "Load failed"));
        } finally {
            setLoading(false);
        }
    }, [runId]);

    const toggle = () => {
        setOpen((o) => !o);
        if (!open && !data) load();
    };

    return (
        <Card
            title={
                <button
                    type="button"
                    onClick={toggle}
                    className="inline-flex items-center gap-2 text-left"
                    data-testid="parameter-provenance-toggle"
                >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <BookOpen className="w-4 h-4 text-ux-accent" />
                    <span>Parameter Provenance</span>
                    {data && (
                        <span className="ml-2 text-[11px] font-mono text-ux-text-3">
                            {data.total_params} params · {Object.keys(data.by_collection || {}).length} collections
                        </span>
                    )}
                </button>
            }
            testId="parameter-provenance-card"
        >
            {!open ? (
                <div className="text-[12px] text-ux-text-3">
                    Immutable snapshot of every Parameter leaf captured at run submit.
                    Click to expand.
                </div>
            ) : loading ? (
                <div className="text-[12px] text-ux-text-3">Loading…</div>
            ) : err ? (
                <div className="text-[12px] text-ux-danger" data-testid="parameter-provenance-error">
                    {err}
                </div>
            ) : data && data.total_params === 0 ? (
                <div className="text-[12px] text-ux-text-3">
                    {data.note || "No parameters captured for this run."}
                </div>
            ) : data ? (
                <div className="space-y-4" data-testid="parameter-provenance-body">
                    {Object.entries(data.by_collection || {}).map(([coll, rows]) => (
                        <div key={coll}>
                            <div className="text-[11px] font-mono uppercase tracking-wider text-ux-accent mb-1">
                                {coll} <span className="text-ux-text-3">({rows.length})</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-[12px] font-mono" data-testid={`provenance-table-${coll}`}>
                                    <thead>
                                        <tr className="text-left text-ux-text-3 border-b border-ux-border-1">
                                            <th className="py-1 pr-2">Field</th>
                                            <th className="py-1 pr-2">Value</th>
                                            <th className="py-1 pr-2">Source</th>
                                            <th className="py-1 pr-2">Confidence</th>
                                            <th className="py-1 pr-2">Citation</th>
                                            <th className="py-1">Updated</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={`${coll}-${i}`} className="border-b border-ux-border-1/40">
                                                <td className="py-1 pr-2 text-ux-text-2 truncate max-w-[260px]">
                                                    {r.field_path}
                                                </td>
                                                <td className="py-1 pr-2 tabular-nums text-ux-text-1">
                                                    {renderValue(r)}
                                                </td>
                                                <td className="py-1 pr-2"><SourceChip source={r.source} /></td>
                                                <td className="py-1 pr-2"><ConfidenceChip level={r.confidence} /></td>
                                                <td className="py-1 pr-2 text-ux-text-3 truncate max-w-[240px]">
                                                    {r.citation || "—"}
                                                </td>
                                                <td className="py-1 text-ux-text-3 text-[10px] whitespace-nowrap">
                                                    {r.updated_by || "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                    <div className="text-[10px] text-ux-text-3 font-mono pt-2 border-t border-ux-border-1">
                        Captured: {data.captured_at || "—"} · Immutable
                    </div>
                </div>
            ) : null}
            {!open && (
                <div className="mt-2">
                    <SpinnerButton
                        size="sm"
                        variant="ghost"
                        loading={loading}
                        onClick={toggle}
                        testId="parameter-provenance-load"
                    >
                        Load snapshot
                    </SpinnerButton>
                </div>
            )}
        </Card>
    );
}
